import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Datenschutz = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Datenschutzerklärung - Adloc</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#FAF3E0] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zur Startseite
          </Button>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Datenschutzerklärung</h1>

            <div className="space-y-8 text-gray-700">
              {/* Section 1 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Datenschutz auf einen Blick</h2>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Allgemeine Hinweise</h3>
                <p className="leading-relaxed mb-4">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
                  passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
                  persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen 
                  Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Datenerfassung auf dieser Website</h3>
                <p className="font-bold mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</p>
                <p className="leading-relaxed mb-4">
                  Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                  können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
                </p>

                <p className="font-bold mb-2">Wie erfassen wir Ihre Daten?</p>
                <p className="leading-relaxed mb-4">
                  Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um 
                  Daten handeln, die Sie in ein Kontaktformular eingeben oder bei der Registrierung angeben.
                </p>
                <p className="leading-relaxed mb-4">
                  Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme 
                  erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des 
                  Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.
                </p>

                <p className="font-bold mb-2">Wofür nutzen wir Ihre Daten?</p>
                <p className="leading-relaxed mb-4">
                  Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                  Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
                </p>

                <p className="font-bold mb-2">Welche Rechte haben Sie bezüglich Ihrer Daten?</p>
                <p className="leading-relaxed mb-4">
                  Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer 
                  gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder 
                  Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, 
                  können Sie diese Einwilligung jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter 
                  bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. 
                  Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Hosting</h2>
                <p className="leading-relaxed mb-4">
                  Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
                </p>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Externes Hosting</h3>
                <p className="leading-relaxed mb-4">
                  Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, 
                  werden auf den Servern des Hosters / der Hoster gespeichert. Hierbei kann es sich v.a. um IP-Adressen, 
                  Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und 
                  sonstige Daten, die über eine Website generiert werden, handeln.
                </p>
                <p className="leading-relaxed mb-4">
                  Das externe Hosting erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren potenziellen und 
                  bestehenden Kunden (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer sicheren, schnellen und 
                  effizienten Bereitstellung unseres Online-Angebots durch einen professionellen Anbieter (Art. 6 Abs. 1 
                  lit. f DSGVO). Sofern eine entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung 
                  ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TTDSG, soweit die 
                  Einwilligung die Speicherung von Cookies oder den Zugriff auf Informationen im Endgerät des Nutzers 
                  (z.B. Device-Fingerprinting) im Sinne des TTDSG umfasst.
                </p>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Allgemeine Hinweise und Pflichtinformationen</h2>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Datenschutz</h3>
                <p className="leading-relaxed mb-4">
                  Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre 
                  personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie 
                  dieser Datenschutzerklärung.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Hinweis zur verantwortlichen Stelle</h3>
                <p className="leading-relaxed mb-4">
                  Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
                </p>
                <p className="leading-relaxed mb-4">
                  Atilla Ciftcioglu<br />
                  Hüttenstraße 100<br />
                  56170 Bendorf<br />
                  E-Mail: kontakt@adloc.io
                </p>
                <p className="leading-relaxed mb-4">
                  Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen 
                  über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten (z.B. Namen, E-Mail-Adressen o.Ä.) 
                  entscheidet.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Speicherdauer</h3>
                <p className="leading-relaxed mb-4">
                  Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, verbleiben 
                  Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt. Wenn Sie ein 
                  berechtigtes Löschersuchen geltend machen oder eine Einwilligung zur Datenverarbeitung widerrufen, 
                  werden Ihre Daten gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe für die Speicherung 
                  Ihrer personenbezogenen Daten haben (z.B. steuer- oder handelsrechtliche Aufbewahrungsfristen).
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
                <p className="leading-relaxed mb-4">
                  Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine 
                  bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten 
                  Datenverarbeitung bleibt vom Widerruf unberührt.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">
                  Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen sowie gegen Direktwerbung (Art. 21 DSGVO)
                </h3>
                <p className="leading-relaxed mb-4 uppercase font-bold">
                  WENN DIE DATENVERARBEITUNG AUF GRUNDLAGE VON ART. 6 ABS. 1 LIT. E ODER F DSGVO ERFOLGT, HABEN SIE 
                  JEDERZEIT DAS RECHT, AUS GRÜNDEN, DIE SICH AUS IHRER BESONDEREN SITUATION ERGEBEN, GEGEN DIE 
                  VERARBEITUNG IHRER PERSONENBEZOGENEN DATEN WIDERSPRUCH EINZULEGEN; DIES GILT AUCH FÜR EIN AUF DIESE 
                  BESTIMMUNGEN GESTÜTZTES PROFILING. DIE JEWEILIGE RECHTSGRUNDLAGE, AUF DENEN EINE VERARBEITUNG BERUHT, 
                  ENTNEHMEN SIE DIESER DATENSCHUTZERKLÄRUNG. WENN SIE WIDERSPRUCH EINLEGEN, WERDEN WIR IHRE BETROFFENEN 
                  PERSONENBEZOGENEN DATEN NICHT MEHR VERARBEITEN, ES SEI DENN, WIR KÖNNEN ZWINGENDE SCHUTZWÜRDIGE GRÜNDE 
                  FÜR DIE VERARBEITUNG NACHWEISEN, DIE IHRE INTERESSEN, RECHTE UND FREIHEITEN ÜBERWIEGEN ODER DIE 
                  VERARBEITUNG DIENT DER GELTENDMACHUNG, AUSÜBUNG ODER VERTEIDIGUNG VON RECHTSANSPRÜCHEN (WIDERSPRUCH 
                  NACH ART. 21 ABS. 1 DSGVO).
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Beschwerderecht bei der zuständigen Aufsichtsbehörde</h3>
                <p className="leading-relaxed mb-4">
                  Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer 
                  Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres Arbeitsplatzes 
                  oder des Orts des mutmaßlichen Verstoßes zu. Das Beschwerderecht besteht unbeschadet anderweitiger 
                  verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Recht auf Datenübertragbarkeit</h3>
                <p className="leading-relaxed mb-4">
                  Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags 
                  automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format 
                  aushändigen zu lassen. Sofern Sie die direkte Übertragung der Daten an einen anderen Verantwortlichen 
                  verlangen, erfolgt dies nur, soweit es technisch machbar ist.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Auskunft, Löschung und Berichtigung</h3>
                <p className="leading-relaxed mb-4">
                  Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf unentgeltliche 
                  Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck 
                  der Datenverarbeitung und ggf. ein Recht auf Berichtigung oder Löschung dieser Daten. Hierzu sowie zu 
                  weiteren Fragen zum Thema personenbezogene Daten können Sie sich jederzeit an uns wenden.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Recht auf Einschränkung der Verarbeitung</h3>
                <p className="leading-relaxed mb-4">
                  Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. 
                  Hierzu können Sie sich jederzeit an uns wenden. Das Recht auf Einschränkung der Verarbeitung besteht 
                  in folgenden Fällen:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Wenn Sie die Richtigkeit Ihrer bei uns gespeicherten personenbezogenen Daten bestreiten</li>
                  <li>Wenn die Verarbeitung Ihrer Daten unrechtmäßig geschah/geschieht</li>
                  <li>Wenn wir Ihre Daten nicht mehr benötigen, Sie sie jedoch zur Ausübung, Verteidigung oder 
                    Geltendmachung von Rechtsansprüchen benötigen</li>
                  <li>Wenn Sie Widerspruch nach Art. 21 Abs. 1 DSGVO eingelegt haben</li>
                </ul>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Datenerfassung auf dieser Website</h2>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Registrierung auf dieser Website</h3>
                <p className="leading-relaxed mb-4">
                  Sie können sich auf dieser Website registrieren, um zusätzliche Funktionen auf der Seite zu nutzen. 
                  Die dazu eingegebenen Daten verwenden wir nur zum Zwecke der Nutzung des jeweiligen Angebotes oder 
                  Dienstes, für den Sie sich registriert haben. Die bei der Registrierung abgefragten Pflichtangaben 
                  müssen vollständig angegeben werden. Anderenfalls werden wir die Registrierung ablehnen.
                </p>
                <p className="leading-relaxed mb-4">
                  Für wichtige Änderungen etwa beim Angebotsumfang oder bei technisch notwendigen Änderungen nutzen 
                  wir die bei der Registrierung angegebene E-Mail-Adresse, um Sie auf diesem Wege zu informieren.
                </p>
                <p className="leading-relaxed mb-4">
                  Die Verarbeitung der bei der Registrierung eingegebenen Daten erfolgt zum Zwecke der Durchführung des 
                  durch die Registrierung begründeten Nutzungsverhältnisses und ggf. zur Anbahnung weiterer Verträge 
                  (Art. 6 Abs. 1 lit. b DSGVO).
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Server-Log-Dateien</h3>
                <p className="leading-relaxed mb-4">
                  Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, 
                  die Ihr Browser automatisch an uns übermittelt. Dies sind:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Browsertyp und Browserversion</li>
                  <li>Verwendetes Betriebssystem</li>
                  <li>Referrer URL</li>
                  <li>Hostname des zugreifenden Rechners</li>
                  <li>Uhrzeit der Serveranfrage</li>
                  <li>IP-Adresse</li>
                </ul>
                <p className="leading-relaxed mb-4">
                  Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung dieser 
                  Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes 
                  Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner Website.
                </p>
              </section>

              {/* Section 5 - Analytics & Tracking */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Analyse-Tools und Werbung</h2>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Google Analytics</h3>
                <p className="leading-relaxed mb-4">
                  Diese Website nutzt Funktionen des Webanalysedienstes Google Analytics. Anbieter ist die Google Ireland 
                  Limited („Google"), Gordon House, Barrow Street, Dublin 4, Irland.
                </p>
                <p className="leading-relaxed mb-4">
                  Google Analytics ermöglicht es dem Websitebetreiber, das Verhalten der Websitebesucher zu analysieren. 
                  Hierbei erhält der Websitebetreiber verschiedene Nutzungsdaten, wie z.B. Seitenaufrufe, Verweildauer, 
                  verwendete Betriebssysteme und Herkunft des Nutzers. Diese Daten werden dem jeweiligen Endgerät des 
                  Users zugeordnet. Eine Zuordnung zu einer User-ID erfolgt nicht.
                </p>
                <p className="leading-relaxed mb-4">
                  Des Weiteren können wir mit Google Analytics u.a. Ihre Maus- und Scrollbewegungen und Klicks aufzeichnen. 
                  Ferner verwendet Google Analytics verschiedene Modellierungsansätze, um die erfassten Datensätze zu 
                  ergänzen und setzt Machine-Learning-Technologien bei der Datenanalyse ein.
                </p>
                <p className="leading-relaxed mb-4">
                  Die Nutzung dieses Dienstes erfolgt auf Grundlage Ihrer Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO 
                  und § 25 Abs. 1 TTDSG. Die Einwilligung ist jederzeit widerrufbar.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Meta Pixel (Facebook Pixel)</h3>
                <p className="leading-relaxed mb-4">
                  Diese Website nutzt zur Konversionsmessung das Besucheraktions-Pixel von Meta (ehemals Facebook). 
                  Anbieter dieses Dienstes ist die Meta Platforms Ireland Limited, 4 Grand Canal Square, Dublin 2, Irland. 
                  Die erfassten Daten werden nach Aussage von Meta jedoch auch in die USA und in andere Drittländer übertragen.
                </p>
                <p className="leading-relaxed mb-4">
                  So kann das Verhalten der Seitenbesucher nachverfolgt werden, nachdem diese durch Klick auf eine 
                  Meta-Werbeanzeige auf die Website des Anbieters weitergeleitet wurden. Dadurch können die Wirksamkeit 
                  der Meta-Werbeanzeigen für statistische und Marktforschungszwecke ausgewertet werden und zukünftige 
                  Werbemaßnahmen optimiert werden.
                </p>
                <p className="leading-relaxed mb-4">
                  Die erhobenen Daten sind für uns als Betreiber dieser Website anonym, wir können keine Rückschlüsse auf 
                  die Identität der Nutzer ziehen. Die Daten werden aber von Meta gespeichert und verarbeitet, sodass eine 
                  Verbindung zum jeweiligen Nutzerprofil möglich ist und Meta die Daten für eigene Werbezwecke, entsprechend 
                  der Meta-Datenverwendungsrichtlinie verwenden kann.
                </p>
                <p className="leading-relaxed mb-4">
                  Die Nutzung dieses Dienstes erfolgt auf Grundlage Ihrer Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO 
                  und § 25 Abs. 1 TTDSG. Die Einwilligung ist jederzeit widerrufbar.
                </p>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Verwendete B2B-Kontaktdaten</h2>
                <p className="leading-relaxed mb-4">
                  Die auf dieser Plattform bereitgestellten B2B-Kontaktdaten (Firmennamen, Adressen, Telefonnummern, 
                  E-Mail-Adressen, Websites) stammen aus öffentlich zugänglichen Quellen wie der Google Places API und 
                  anderen öffentlichen Verzeichnissen.
                </p>
                <p className="leading-relaxed mb-4">
                  Diese Daten werden ausschließlich zu geschäftlichen Zwecken (B2B) bereitgestellt und dienen der 
                  Kontaktaufnahme zwischen Unternehmen. Die Nutzung dieser Daten für private Zwecke oder den Versand 
                  von Massen-E-Mails ohne vorherige Einwilligung ist nicht gestattet und liegt in der Verantwortung 
                  des Nutzers.
                </p>
                <p className="leading-relaxed mb-4">
                  Wir bemühen uns, die Daten aktuell und korrekt zu halten, übernehmen jedoch keine Gewähr für die 
                  Vollständigkeit und Richtigkeit der bereitgestellten Informationen.
                </p>
              </section>

              {/* Final note */}
              <section className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Stand: November 2025
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Diese Datenschutzerklärung wurde mit Hilfe von Muster-Vorlagen erstellt und an die spezifischen 
                  Anforderungen dieser Website angepasst.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Datenschutz;
